<template>
  <q-page class="main-wrapper-page single-page" style="background-color: var(--q-accent)">
    <div class="page-head q-mb-lg">
      <section class="widget-section widget-section--filter full-width flex no-wrap items-center q-mb-lg">
        <q-form ref="form" class="full-width">
          <div class="row no-wrap full-width q-col-gutter-sm">
            <div class="col">
              <q-input
                v-model="filters.id"
                label="ID"
                outlined
                dense
                clearable
                bg-color="white"
                @update:model-value="filterTable"
                @clear="reloadTable()"
              />
            </div>
            <div class="col">
              <q-input
                v-model="filters.uuid"
                label="UUID"
                :rules="[validateUuid]"
                outlined
                dense
                clearable
                bg-color="white"
                @update:model-value="filterTable"
                @clear="reloadTable()"
              />
            </div>
            <div class="col">
              <q-input
                v-model="filters.schema"
                @update:model-value="filterTable"
                label="Schema"
                outlined
                dense
                clearable
                bg-color="white"
                @clear="reloadTable()"
              />
            </div>
          </div>
          <div class="row no-wrap full-width q-col-gutter-sm">
            <div class="col flex no-wrap q-col-gutter-sm">
              <DatePicker
                v-model="filters.dateFrom"
                @update:model-value="filterTable"
                label="Created after"
                class="full-width"
                @clear="
                  () => {
                    filters.dateFrom = undefined;
                  }
                "
              />
            </div>
            <div class="col flex no-wrap q-col-gutter-sm">
              <DatePicker
                v-model="filters.dateTo"
                @update:model-value="filterTable"
                label="Created before"
                class="full-width"
                @clear="
                  () => {
                    filters.dateTo = undefined;
                  }
                "
              />
            </div>
            <div class="col-2 flex no-wrap q-col-gutter-sm">
              <q-checkbox
                v-model="filters.deleted"
                @update:model-value="filterTable"
                label="Show deleted"
                class="full-width"
              />
            </div>
          </div>
        </q-form>
        <q-icon @click="resetFilters" name="engp:engp-ico-restart" class="q-ml-md cursor-pointer" size="20px">
          <q-tooltip>Reset</q-tooltip>
        </q-icon>
      </section>
      <section class="widget-section widget-section--btn q-ml-md">
        <q-btn color="primary" @click="$router.push(`/authorization-schemas/new`)" no-caps class="btn--no-hover"
          >New Auth Schema</q-btn
        >
      </section>
    </div>

    <section class="widget-section widget-section--table">
      <AuthorizationSchemasTable
        ref="authSchemasTable"
        :rowsPerPageOptions="[20, 50, 100]"
        class="fixed-table"
        :filters="filters"
        @confirmDeleteSchema="showConfirm($event)"
      />
    </section>
  </q-page>

  <q-dialog v-model="showConfirmDialog" class="single-service-page" persistent>
    <q-card style="min-width: 480px; border-radius: 0px">
      <q-toolbar class="shadow-2 q-py-sm q-px-md">
        <q-toolbar-title class="text-weight-bold">
          <strong>Delete Authorization Schema</strong>
        </q-toolbar-title>
        <q-btn icon="close" style="min-width: 20px" round flat class="btn--no-hover" @click="closeDeleteDialog"></q-btn>
      </q-toolbar>
      <q-card-section class="q-mx-md q-px-none q-pb-none" style="border-top: 2px solid var(--q-primary)">
        <p>Are you sure you want to delete this authorization schema?</p>
        <p v-if="deleteAlternative">Select alternative authorization schema for blueprints that use this schema</p>
        <q-select
          popup-content-class="alternative-id-popup"
          v-if="deleteAlternative"
          v-model="alternativeUuid"
          :options="filteredAlternativeOptions"
          @update:model-value="validateDeleteWorkflow"
          outlined
          dense
          label="UUID"
          class="q-mt-md q-mb-lg full-width"
        ></q-select>
      </q-card-section>
      <q-card-actions class="q-px-md q-pt-none q-pb-lg">
        <q-btn
          style="border-radius: 10px; text-transform: capitalize"
          color="primary"
          text-color="text-color"
          :loading="deleteLoading"
          :disabled="!canBeDelete || deleteLoading || (deleteAlternative && !alternativeUuid) || deleteErrorMessage"
          @click="deleteSchema"
          >Yes<q-tooltip max-width="480px" style="white-space: pre-line; z-index: 99999" v-if="deleteErrorMessage">{{
            deleteErrorMessage
          }}</q-tooltip></q-btn
        >
        <q-btn
          style="border-radius: 10px; text-transform: capitalize"
          class="btn--no-hover"
          flat
          @click="closeDeleteDialog"
          >No</q-btn
        >
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script>
import DatePicker from '../allItems/components/DatePicker.vue';
import AuthorizationSchemasTable from './components/AuthorizationSchemasTable.vue';
import { createWorkFlow, validateWorkFlow } from '../registrations/api/api';
import { getAuthorizationSchemas } from './api/api';

export default {
  name: 'AuthorizationSchemasPage',
  components: {
    DatePicker,
    AuthorizationSchemasTable,
  },
  data() {
    return {
      filters: {
        id: undefined,
        uuid: undefined,
        deleted: false,
        schema: undefined,
        dateFrom: undefined,
        dateTo: undefined,
      },
      canBeDelete: false,
      deleteErrorMessage: undefined,
      loading: false,
      timeout: undefined,
      showConfirmDialog: false,
      currentSchema: undefined,
      deleteLoading: false,
      deleteAlternative: false,
      alternativeUuid: undefined,
      deleteAlternativeOptions: [],
    };
  },
  computed: {
    filteredAlternativeOptions() {
      const options = this.currentSchema
        ? this.deleteAlternativeOptions.filter((option) => option.value !== this.currentSchema.uuid)
        : this.deleteAlternativeOptions;
      return [...options].sort((a, b) => a.label.localeCompare(b.label));
    },
  },
  methods: {
    validateUuid(value) {
      const uuidRegex =
        // eslint-disable-next-line
        /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      return value?.length ? uuidRegex.test(value) || 'Must be a valid UUID' : true;
    },
    async deleteSchema() {
      this.deleteLoading = true;
      const deleteSchemaWorkflow = {
        workflowId: 'engprt.delete-auth-schema',
        inputVariables: {
          uuid: this.currentSchema.uuid,
          ...(this.deleteAlternative && this.alternativeUuid
            ? { alternativeAuthSchemaUuid: this.alternativeUuid.value }
            : {}),
        },
      };
      try {
        await createWorkFlow(deleteSchemaWorkflow);
        this.$notify('positive', 'Success', 'Authorization schema will be deleted.');
        this.reloadTable();
      } catch (error) {
        this.$notify('negative', 'Error', error.message);
      } finally {
        this.deleteLoading = false;
        this.currentSchema = undefined;
        this.deleteAlternative = false;
        this.alternativeUuid = undefined;
        this.showConfirmDialog = false;
      }
    },
    closeDeleteDialog() {
      this.currentSchema = undefined;
      this.deleteAlternative = false;
      this.alternativeUuid = undefined;
      this.showConfirmDialog = false;
      this.deleteErrorMessage = undefined;
    },
    async validateDeleteWorkflow() {
      if (!this.currentSchema) return;
      const data = {
        workflowId: 'engprt.delete-auth-schema',
        inputVariables: {
          uuid: this.currentSchema.uuid,
          alternativeAuthSchemaUuid: this.alternativeUuid?.value,
        },
      };
      this.deleteLoading = true;
      await validateWorkFlow(data)
        .then(() => {
          this.canBeDelete = true;
          this.deleteErrorMessage = undefined;
        })
        .catch((error) => {
          this.deleteErrorMessage = error.message;
        })
        .finally(() => {
          this.deleteLoading = false;
        });
    },
    async showConfirm(item) {
      this.currentSchema = item;
      this.showConfirmDialog = true;
      const data = {
        workflowId: 'engprt.delete-auth-schema',
        inputVariables: {
          uuid: item.uuid,
        },
      };
      this.deleteLoading = true;
      await validateWorkFlow(data)
        .then(() => {
          this.canBeDelete = true;
          this.deleteErrorMessage = undefined;
        })
        .catch((error) => {
          if (error.message.includes('alternativeAuthSchemaUuid')) {
            this.deleteAlternative = true;
          }
          this.deleteErrorMessage = error.message;
        })
        .finally(() => {
          this.deleteLoading = false;
        });
    },
    resetFilters() {
      Object.keys(this.filters).forEach((key) => {
        this.filters[key] = undefined;
      });
      this.filters.deleted = false;
      this.reloadTable();
    },
    filterTable() {
      this.$refs.form.validate().then((valid) => {
        if (valid) {
          this.loading = true;
          if (this.timeout) clearTimeout(this.timeout);
          this.timeout = setTimeout(async () => {
            this.reloadTable();
          }, 1500);
        }
      });
    },
    reloadTable() {
      this.$refs.authSchemasTable?.reload();
    },
  },
  async mounted() {
    this.deleteAlternativeOptions = await getAuthorizationSchemas(1000, 0, 'createdAt', true).then((res) =>
      res.data.map((item) => ({
        label: item.id,
        value: item.uuid,
      })),
    );
    const query = this.$route.query;

    Object.keys(this.filters).forEach((key) => {
      this.filters[key] = query[key] || undefined;
      if (key === 'deleted') {
        this.filters[key] = query[key] === 'true';
      }
    });
    this.reloadTable();
  },
};
</script>

<style lang="scss" scoped>
.widget-section {
  padding: 16px 16px 16px 5px;
  margin-bottom: 0;
}
.widget-section--btn {
  padding: 16px;
  min-width: 180px;
}
.showBanner {
  .fixed-table {
    height: calc(100vh - 360px);
  }
}
.fixed-table {
  height: calc(100vh - 300px);
}
.page-head {
  display: flex;
}
</style>
